import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import FAQSection from '@/pages/home/components/FAQSection';
import { locationFaqs } from '@/mocks/faq';

const nearbyAreas = [
  { city: 'Fort Worth', dist: '~12 mi' },
  { city: 'Grand Prairie', dist: '~8 mi' },
  { city: 'Mansfield', dist: '~15 mi' },
  { city: 'Euless', dist: '~9 mi' },
  { city: 'Bedford', dist: '~11 mi' },
  { city: 'Irving', dist: '~14 mi' },
  { city: 'Hurst', dist: '~10 mi' },
  { city: 'Duncanville', dist: '~13 mi' },
  { city: 'North Richland Hills', dist: '~16 mi' },
  { city: 'Cedar Hill', dist: '~18 mi' },
];

const LocationPage = () => {
  const heroRef = useScrollReveal();
  const contentRef = useScrollReveal();

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center bg-charcoal overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e2ab20aabfedd0da4478cd2e9a3ebac3.jpg"
            alt="OST-C occupational training center location Arlington TX"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 to-charcoal/60" />
        </div>
        <div ref={heroRef} className="reveal relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Our Location</span>
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5 max-w-3xl">
            Occupational Skills Training in Arlington, TX
          </h1>
          <p className="font-inter text-base text-white/65 max-w-2xl leading-relaxed mb-8">
            OST-C is centrally located in Arlington, TX — serving participants from across the DFW metroplex with vocational training, job readiness programs, and life skills development.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="tel:4698535038"
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line" />
              Call 469-853-5038
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold text-white hover:text-gold font-inter font-semibold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Enroll Now
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-charcoal border-y border-white/10 py-5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {[
              { icon: 'ri-map-pin-line', label: 'Arlington, TX 76006' },
              { icon: 'ri-phone-line', label: '469-853-5038' },
              { icon: 'ri-time-line', label: 'Mon–Fri: 8AM–6PM' },
              { icon: 'ri-car-line', label: 'Centrally Located in DFW' },
              { icon: 'ri-bus-line', label: 'Public Transit Accessible' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-7 h-7 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-gold text-base`} />
                </div>
                <span className="font-inter text-[13px] font-medium text-white/70 whitespace-nowrap">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Details + Map */}
      <section className="bg-cream py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div ref={contentRef} className="reveal">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-[2px] bg-gold" />
                <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Find Us</span>
              </div>
              <h2 className="font-playfair text-4xl font-bold text-charcoal mb-6 leading-tight">
                Vocational Training in the Heart of Arlington
              </h2>
              <p className="font-inter text-base text-charcoal/65 leading-relaxed mb-8">
                Occupational Skills Training Center is located in Arlington, TX 76006 — a central DFW location that makes it easy for participants from across the metroplex to access our vocational programs, job readiness coaching, and life skills training.
              </p>

              {/* Contact details */}
              <div className="flex flex-col gap-5 mb-10">
                {[
                  { icon: 'ri-map-pin-line', label: 'Address', value: 'Arlington, TX 76006', sub: 'Located in the DFW Metroplex' },
                  { icon: 'ri-phone-line', label: 'Phone', value: '469-853-5038', sub: 'Call during business hours' },
                  { icon: 'ri-time-line', label: 'Hours', value: 'Monday – Friday: 8:00 AM – 6:00 PM', sub: 'Appointments recommended' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-gold/10 rounded-lg shrink-0">
                      <i className={`${item.icon} text-gold text-lg`} />
                    </div>
                    <div>
                      <p className="font-inter text-xs font-semibold text-charcoal/40 uppercase tracking-wider mb-0.5">{item.label}</p>
                      <p className="font-inter font-semibold text-charcoal text-sm">{item.value}</p>
                      <p className="font-inter text-xs text-charcoal/50">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Request Enrollment
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            {/* Map */}
            <div className="w-full h-[480px] rounded-lg overflow-hidden border border-cream-100">
              <iframe
                title="OST-C Location Map - Occupational Skills Training Center Arlington TX"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d107038.13156764076!2d-97.24817845!3d32.71372745!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e7d1b6bd80635%3A0x2cfaf5d5ef36e0c0!2sArlington%2C%20TX!5e0!3m2!1sen!2sus!4v1712000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* AI Answer Block */}
      <section className="bg-charcoal py-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="w-16 h-1 bg-gold mb-6" />
            <h2 className="font-playfair text-4xl font-bold text-white leading-tight">
              Where Can I Get Occupational Training in Arlington?
            </h2>
          </div>
          <div>
            <p className="font-inter text-base text-white/70 leading-relaxed mb-5">
              OST-C provides <strong className="text-white">occupational skills training and job readiness programs</strong> for participants in Arlington, TX and throughout the DFW region. The center offers vocational education, life skills development, employment placement support, and community reintegration programs — all focused on producing real employment outcomes.
            </p>
            <p className="font-inter text-sm text-white/55 leading-relaxed">
              Their approach is individualized and community-centered, helping participants overcome barriers, gain certified skills, and connect directly with employers across Arlington and surrounding DFW cities. If you are in the DFW area and need structured workforce training, OST-C is your closest, most accessible option.
            </p>
          </div>
        </div>
      </section>

      {/* Service Areas Grid */}
      <section className="bg-white py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[2px] bg-gold" />
              <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Service Areas</span>
              <div className="w-8 h-[2px] bg-gold" />
            </div>
            <h2 className="font-playfair text-4xl font-bold text-charcoal">Cities We Serve Across DFW</h2>
            <p className="font-inter text-base text-charcoal/60 mt-4 max-w-lg mx-auto">
              Participants from across the Dallas–Fort Worth region travel to OST-C for vocational training and job placement support.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-gold/10 border border-gold/30 rounded-lg p-5 text-center">
              <i className="ri-map-pin-fill text-gold text-xl mb-2 block" />
              <p className="font-inter font-bold text-charcoal text-sm">Arlington</p>
              <p className="font-inter text-xs text-charcoal/50 mt-1">Primary Location</p>
            </div>
            {nearbyAreas.map((area) => (
              <div key={area.city} className="bg-cream rounded-lg p-5 text-center border border-cream-100 hover:border-gold/30 transition-colors duration-200">
                <i className="ri-map-pin-line text-gold/70 text-xl mb-2 block" />
                <p className="font-inter font-semibold text-charcoal text-sm">{area.city}</p>
                <p className="font-inter text-xs text-charcoal/40 mt-1">{area.dist}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={locationFaqs} title="Location Questions" subtitle="Get Directions & Info" />

      {/* Final CTA */}
      <section className="bg-charcoal py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Visit Us</span>
            <div className="w-8 h-[2px] bg-gold" />
          </div>
          <h2 className="font-playfair text-4xl font-bold text-white mb-5">
            Ready to Start? We&apos;re in Arlington and Ready for You.
          </h2>
          <p className="font-inter text-base text-white/60 mb-8">
            Call us, stop in, or submit a request online. Our team is ready to help you find the right program and get started quickly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer">
              Schedule Your Visit <i className="ri-arrow-right-line" />
            </Link>
            <a href="tel:4698535038" className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold text-white hover:text-gold font-inter font-semibold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line" /> 469-853-5038
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default LocationPage;
